import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDialogs } from "@toolpad/core";
import {
  AggregateScoreType,
  CaseStatus,
  GetAffectedAssetsDocument,
  GetAggregateScoresDocument,
  GetCasesCountDocument,
  ScoreState,
  UiPage,
  useGetAggregateScoresQuery,
  useGetCasesCountQuery,
  useLoadMqueryQuery,
  useLoadPolicyQuery,
} from "~/operations";
import { PolicyIdToMrn } from "~/lib/mrn";
import { useFindingRiskFactors } from "~/pages/space/security/components/RiskFactors/hooks/useFindingRiskFactors";
import { useTickets } from "~/pages/tickets/hooks";
import { getPolicyPropertiesTabUrl } from "~/lib/urlUtils";
import { SourcesProps } from "~/components/vulnerabilities";
import { VariantIcon } from "~/components/ui-library";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { getCheckBackButtonHref } from "~/pages/space/security/components/Check/utils";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { CheckProps } from "~/pages/space/security/components/Check/Check";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { CreateTicketDialog } from "~/pages/tickets/components/CreateTicketDialog";
import { CreateTicketDialogPayload } from "~/pages/tickets/components/CreateTicketDialog/CreateTicketDialog";
import { useSelection } from "~/hooks/useSelection";
import { useTicketingIntegrations } from "~/pages/tickets/hooks/useTicketingIntegrations";
import { SCORE_STATE_FILTER_KEY } from "~/pages/inventory/asset/useAssetVulnerabilitiesFilterContext";
import { SelectedEntityWithExposedAssetData } from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { CreateSecurityPipelinePrDialog } from "~/pages/integrations/security-pipeline/components/CreatePullRequest/CreateSecurityPipelinePRDialog";
import { useReportViewed } from "~/hooks/useReportViewed";

export function useCheck({ scope, space }: CheckProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const policyId = searchParams.get("policyId") || "";
  const findingMrn = searchParams.get("findingMrn") || "";
  const frameworkMrn = searchParams.get("frameworkMrn") || "";
  const controlMrn = searchParams.get("controlMrn") || "";
  const { controlId, frameworkId } = useParams();
  const dialogs = useDialogs();

  // In case we navigate here from policy checks list -> policyId is there, otherwise we don't wanna make request
  const { data: policyData } = useLoadPolicyQuery({
    variables: { input: { mrn: PolicyIdToMrn(policyId), spaceMrn: space.mrn } },
    skip: !policyId,
  });

  const {
    data: checkData,
    loading: checkLoading,
    error: checkError,
  } = useLoadMqueryQuery({
    variables: {
      input: {
        mrn: findingMrn,
      },
    },
  });

  const {
    data: aggData,
    loading: aggLoading,
    error: aggError,
  } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: scope.mrn || "",
      filter: {
        findingMrn: findingMrn,
        scoreType: AggregateScoreType.Check,
      },
    },
    skip: !scope.mrn,
  });

  useEffect(() => {
    searchParams.set(SCORE_STATE_FILTER_KEY, ScoreState.Open);
    setSearchParams(searchParams, { replace: true });
  }, []);

  // Send report viewed mutation on mount and when the pathname changes
  useReportViewed({
    page: UiPage.Finding,
    pageLoaded: !!findingMrn,
    assetMrn: findingMrn,
    spaceMrn: space.mrn,
    tab: "check",
  });

  const aggScores = aggData?.aggregateScores;
  const aggScoreEdges =
    aggScores?.__typename === "AggregateScoresConnection"
      ? aggScores.edges
      : [];
  const checkScore = (
    aggScores?.__typename === "AggregateScoresConnection"
      ? aggScoreEdges?.flatMap((edge) => edge?.node ?? [])
      : []
  )?.pop();

  const blastRadius = checkScore?.blastRadius;

  const { riskFactorsWithDocs, loading: riskFactorsLoading } =
    useFindingRiskFactors({
      spaceMrn: space.mrn,
      findingMrn: findingMrn || "",
      scoreType: AggregateScoreType.Check,
      onlyAffected: false,
    });

  const {
    handleCreateCase,
    handleCreateTicketingIntegrationModalOpen,
    hasListTicketsPermissions,
    hasIntegrationCreatePermission,
    hasCreateTicketPermissions,
    loading: createTicketLoading,
  } = useTickets({
    createCaseRefetchQueries: [GetCasesCountDocument],
  });

  const { data: casesData } = useGetCasesCountQuery({
    variables: {
      input: {
        scopeMrn: scope.mrn,
        findingMrns: [findingMrn],
        statuses: [CaseStatus.Open],
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !findingMrn || !hasListTicketsPermissions,
  });

  const { hasTicketingIntegration } = useTicketingIntegrations();

  const linkToPolicyPropertiesTab = getPolicyPropertiesTabUrl({
    policyId,
    spaceId: scope?.id,
  });

  const {
    handleNodeClick,
    handleCancelClick,
    selectedEntities: selectedAssets,
    setSelectedEntities: setSelectedAssets,
  } = useSelection<
    SelectedEntityWithExposedAssetData &
      SelectedEntityWithException &
      SelectedEntityWithFindingData
  >();

  const loading = checkLoading || aggLoading || riskFactorsLoading;

  const check = checkData?.mquery;

  const sourceLinks: SourcesProps["links"] =
    check?.docs?.references?.map((r) => ({ href: r.url, text: r.title })) || [];
  const hasSources = sourceLinks.length > 0;

  const variants = [
    ...(
      check?.variants?.map((variant) => ({
        label: variant?.title || "",
        value: variant?.id || "",
        icon: <VariantIcon type={variant?.icon || ""} />,
      })) || []
    ).sort((a, b) => (a.label > b.label ? 1 : -1)),
  ];
  const selectedVariantId = searchParams.get("variantQuery");
  const selectedVariant =
    variants.find((v) => v.value === selectedVariantId) || variants[0];
  const variantQueries = check?.variantQueries?.flatMap((vq) => vq ?? []) || [];
  const visibleVariantQueries = variantQueries.flatMap((vq) => {
    if (!selectedVariant) return vq;
    return vq.filterID === selectedVariant.value ? [vq] : [];
  });

  const visibleQueries =
    variantQueries.length > 0 ? visibleVariantQueries : check ? [check] : [];

  const {
    handleSetExceptionForScopes,
    handleRemoveExceptionGroups,
    loading: exceptionsLoading,
  } = useExceptions({
    onSetException: () => {
      setSelectedAssets([]);
    },
    multipleScopes: true,
    scopeMrns:
      selectedAssets.length > 0
        ? selectedAssets.map((s) => s.scopeMrn)
        : [scope.mrn],
    queryMrns: [check?.mrn || ""],
    refetchQueries: [GetAggregateScoresDocument, GetAffectedAssetsDocument],
  });

  const handleRemoveExceptionDialogOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedAssets([]);
      },
      exceptionMrns: [checkScore?.exception?.exceptionMrn].filter(
        (mrn): mrn is string => Boolean(mrn),
      ),
    });
  };

  const handleSetExceptionDialogOpen = ({
    selectedAssets,
  }: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
  }) => {
    dialogs.open(SetExceptionDialog, {
      target: "check",
      selectedEntities: selectedAssets,
      targetData: {
        targetTitle: check?.title || "",
      },
      onSave: handleSetExceptionForScopes,
    });
  };

  const handleCreateTicketDialogOpen = ({
    selectedAssets,
    isScopeLevelTicket,
  }: {
    selectedAssets: Array<
      SelectedEntityWithExposedAssetData &
        SelectedEntityWithException &
        SelectedEntityWithFindingData
    >;
    isScopeLevelTicket: boolean;
  }) => {
    const payload: CreateTicketDialogPayload = {
      target: "asset",
      isCombinedTicket: true,
      loading: checkLoading || createTicketLoading,
      onSave: (data) => handleCreateCase({ data }),
      selectedEntities: selectedAssets.map(
        (
          s: SelectedEntityWithExposedAssetData &
            SelectedEntityWithException &
            SelectedEntityWithFindingData,
        ) => ({
          ...s,
          mrn: findingMrn,
        }),
      ),
      space: space,
      scope: scope,
      hasCreateCasePermissions: hasCreateTicketPermissions,
      assetsCount:
        selectedAssets.length === 1 && isScopeLevelTicket
          ? blastRadius?.affected
          : selectedAssets.length,
    };

    dialogs.open(CreateTicketDialog, payload);
  };

  const handleSecurityPipelinePRDialogOpen = () => {
    dialogs.open(CreateSecurityPipelinePrDialog, {
      spaceScope: space,
      aggregateScoresEdges: aggScoreEdges,
    });
  };

  return {
    isLoading: loading || exceptionsLoading || createTicketLoading,
    hasError: checkError || aggError,
    title: check?.title || "",
    enforcementState: checkScore?.enforcementState,
    previewUntil: checkScore?.previewUntil,
    fallbackPath: getCheckBackButtonHref({
      policyId,
      frameworkId,
      frameworkMrn,
      controlId,
      controlMrn,
      searchParams,
    }),
    checkMrn: check?.mrn,
    findingMrn,
    hasException: !!checkScore?.exception,
    hasTicketingIntegration,
    linkToException: getExceptionLink({
      id: checkScore?.exception?.exceptionId || "",
      queryParams: scope.params,
    }),
    hasVariants: check?.variants && check.variants.length > 0,
    variants,
    selectedVariant,
    searchParams,
    setSearchParams,
    hasListTicketsPermissions,
    hasCreateTicketPermissions,
    hasIntegrationCreatePermission,
    hasTickets: casesData?.cases.totalCount || 0 > 0,
    aggScores,
    checkScore,
    ticketsCount: casesData?.cases.totalCount || 0,
    summary: check?.docs?.desc,
    blastRadius,
    policyId,
    policyData,
    hasSources,
    sourceLinks,
    riskFactorsWithDocs,
    visibleQueries,
    linkToPolicyPropertiesTab,
    selectionProps: {
      selectedAssets,
      handleNodeClick,
      setSelectedAssets,
      handleCancelClick,
    },
    handleRemoveExceptionDialogOpen,
    handleSetExceptionDialogOpen,
    handleCreateTicketDialogOpen,
    handleCreateTicketingIntegrationModalOpen,
    handleSecurityPipelinePRDialogOpen,
  };
}
